import React, { useEffect, useState } from 'react'
import { Button, Checkbox, FormControl, MenuItem, Select, TextField } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { ILoad, ILoadMaterial } from '../../types/load';
import { IManifest, IManifestMaterialAssignment } from '../../types/manifest';
import { ISelectOption } from '../../types/list';

import { ITransporterLight } from '../../types/transporter';
import { IShipmentLight, IShipmentMaterial } from '../../types/shipment';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Json from '../json';

import '../../styles/shared/materialManifestManager.scss';

export interface IMaterialManifestManagerProps {
	manifest?: IManifest;
	lading: ILoad | IShipmentLight;
	materials: ILoadMaterial[] | IShipmentMaterial[];
	manifestVendors: ISelectOption[];
	secondaryTransporters: ITransporterLight[];
	manifestIdBeingEdited?: number | undefined;
	manifestUpdated: (manifest: IManifest, assignments: IManifestMaterialAssignment[]) => void;
	deleteRequested?: (manifest: IManifest) => void;
	editRequested?: (manifestId: number) => void;
	cancelRequested?: (manifestId: number) => void;
}

interface IWorkingMaterial {
	id: number;
	material: ILoadMaterial | IShipmentMaterial;
	manifestId?: number | null;
}

const buildWorkingMaterials = (materials: ILoadMaterial[] | IShipmentMaterial[]) => {
	return (JSON.parse(JSON.stringify(materials)) as ILoadMaterial[] | IShipmentMaterial[]).map(m => { return { id: m.id, material: m, manifestId: m.manifestId } as IWorkingMaterial })
}

const MaterialManifestManager: React.FC<IMaterialManifestManagerProps> = ({ manifest, lading, materials, manifestVendors, secondaryTransporters, manifestIdBeingEdited, manifestUpdated, deleteRequested, editRequested, cancelRequested }) => {

	const confirm = useConfirm();
	const [sortField, setSortField] = useState("material");
	const [sortAscending, setSortAscending] = useState(true);

	const blankManifest: IManifest = {
		id: -1,
		isHazardous: true,
		number: "",
		manifestVendorId: -1,
		secondaryTransporterId: -1,
		customerId: -1,
		remoteShipmentId: -1,
		shipmentId: undefined,
		createdOn: undefined,
		createdBy: undefined
	}
	const [workingMaterials, setWorkingMaterials] = useState<IWorkingMaterial[]>(buildWorkingMaterials(materials));
	const [workingManifest, setWorkingManifest] = useState<IManifest>(JSON.parse(JSON.stringify(manifest ?? blankManifest)));
	const [isEditing, setIsEditing] = useState(workingManifest?.id == -1);
	const [isDataReady, setIsDataReady] = useState(false);
	const [manifestType, setManifestType] = useState<"load" | "shipment" | undefined>(undefined);
	const [materialNameProp, setMaterialNameProp] = useState<string | undefined>(undefined);
	const [materialIsHazardousProp, setMaterialIsHazardousProp] = useState<string | undefined>(undefined);
	const [materialWeightProp, setMaterialWeightProp] = useState<string | undefined>(undefined);
	const [materialUOMAbbreviationProp, setMaterialUOMAbbreviationProp] = useState<string | undefined>(undefined);
	const [positionProp, setPositionProp] = useState<string | undefined>(undefined);
	const [isLadingInEditableStatus, setIsLadingInEditableStatus] = useState(false);

	//#region useEffects
	// Figure out whether this is a load or shipment based on unique properties since we can't get type information in javascript at runtime
	useEffect(() => {
		if (lading) {
			const type = lading.hasOwnProperty("plantWeight") ? "shipment" : "load";
			setManifestType(type);
			setIsLadingInEditableStatus((type === "load" && lading.status == "Loading") || (type === "shipment" && lading.status == "Reconciling"));
		}
	}, [lading])

	useEffect(() => {
		if (manifest) {
			setWorkingManifest(JSON.parse(JSON.stringify(manifest ?? blankManifest)));
		}
	}, [manifest])

	// Get the load, shipment, and customer ID if this is a new manifest
	useEffect(() => {
		if (lading && workingManifest?.id == -1) {
			setWorkingManifest({
				...workingManifest,
				remoteShipmentId: manifestType == "load" ? (lading as ILoad).id : +(lading as IShipmentLight).loadId,
				shipmentId: manifestType == "load" ? null : (lading as IShipmentLight).id,
				customerId: +lading.customerId
			})
		}
	}, [manifestType, workingManifest])

	// Set the appropriate property names for getting material name, weight, and load position based on the manifest type
	useEffect(() => {
		if (manifestType && !isDataReady) {
			setMaterialNameProp(manifestType === "load" ? "materialName" : "vendorMaterialName");
			setMaterialIsHazardousProp(manifestType === "load" ? "isHazardous" : "vendorMaterialIsHazardous");
			setMaterialWeightProp(manifestType === "load" ? "weight" : "vendorAmount");
			setMaterialUOMAbbreviationProp(manifestType === "load" ? "uomAbbreviation" : "vendorUOMAbbreviation");
			setPositionProp(manifestType === "load" ? "row" : "loadPosition");
		}
	}, [manifestType])

	// Start using the property names assigned above when they have all been set
	useEffect(() => {
		if (!isDataReady && manifestType && materialNameProp && materialWeightProp && positionProp) {
			setIsDataReady(true);
		}
	}, [manifestType, materialNameProp, materialWeightProp, positionProp])

	useEffect(() => {
		if (workingManifest.id == -1) return;
		if (!isEditing && manifestIdBeingEdited === workingManifest.id) {
			setIsEditing(true);
		}
		else if (isEditing && manifestIdBeingEdited !== workingManifest.id) {
			setIsEditing(false);
		}
	}, [manifestIdBeingEdited])

	useEffect(() => {
		setWorkingMaterials(buildWorkingMaterials(materials));		
	}, [workingManifest?.isHazardous])

	//Sort
	useEffect(() => {
		if (isDataReady && materials) {
			let newData = buildWorkingMaterials(materials);

			if (sortField) {
				switch (sortField) {
					case "material-weight": {
						// Rows are now formatted like 1A, 12B, etc. Need to split into level and position for proper sorting
						newData.sort((a, b) => {
							const rowParts1 = { weight: a.material[materialWeightProp], unit: a.material[materialUOMAbbreviationProp] };
							const rowParts2 = { weight: b.material[materialWeightProp], unit: b.material[materialUOMAbbreviationProp] };
							return UtilityService.compareTwoFields(rowParts1, rowParts2, 'unit', 'weight', "string", "number");
						});
						break;
					}
					case "tare-weight": {
						newData.sort((a, b) => {
							const tareWeightA = a.material.containerWeight * (a.material.quantity ?? 1);
							const tareWeightB = b.material.containerWeight * (b.material.quantity ?? 1);
							return UtilityService.compareNumbers(tareWeightA, tareWeightB);
						});
						break;
					}
					case "container": {
						newData.sort((a, b) => UtilityService.compareStrings(a.material.containerName, b.material.containerName));
						break;
					}
					case "row": {
						// Rows are now formatted like 1A, 12B, etc. Need to split into level and position for proper sorting
						newData.sort((a, b) => {
							const rowParts1 = { level: a.material[positionProp].substring(a.material[positionProp].length - 1), position: parseInt(a.material[positionProp].substring(0, a.material[positionProp].length - 1)) };
							const rowParts2 = { level: b.material[positionProp].substring(b.material[positionProp].length - 1), position: parseInt(b.material[positionProp].substring(0, b.material[positionProp].length - 1)) };
							return UtilityService.compareTwoFields(rowParts1, rowParts2, 'level', 'position', "string", "number");
						});
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.material[materialNameProp], b.material[materialNameProp]));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setWorkingMaterials(newData);
		}
	}, [isDataReady, materials, sortField, sortAscending])
	//#endregion useEffects

	//#region Event handlers
	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handleDeleteManifest = async () => {
		if (await UtilityService.ConfirmationModal(
			confirm,
			<div className="manifest-delete-dialog-title">
				<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
				Delete Manifest
			</div>,
			<div className="manifest-delete-dialog-content">
				{workingMaterials.filter(m => m.manifestId === workingManifest.id).length > 0 &&
					<>
						All materials assigned to this manifest will return to unassigned status.<br />
						<br />
					</>
				}
				Are you sure you want to delete Manifest <strong>{workingManifest.number}</strong>?
			</div>,
			{
				confirmationText: "Yes, Delete it",
				confirmationButtonProps: { color: 'primary', variant: 'contained' },
				cancellationText: "Cancel"
			}
		) == false) {
			return;
		}

		deleteRequested(workingManifest);
	}

	const handleAddManifest = async () => {
		const assignments = getAssignmentChanges();
		if (assignments.length == 0 && await UtilityService.ConfirmationModal(
			confirm,
			<div className="manifest-delete-dialog-title">
				<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
				Empty Manifest
			</div>,
			<div className="manifest-delete-dialog-content">
				You are creating a manifest that contains no materials.<br />
				<br />
				Proceed with creating an empty manifest?
			</div>,
			{
				confirmationText: "Yes, Create it",
				confirmationButtonProps: { color: 'primary', variant: 'contained' },
				cancellationText: "Cancel"
			}
		) == false) {
			return;
		}
		manifestUpdated(workingManifest, getAssignmentChanges());
	}

	const handleEditManifest = async () => {
		editRequested(workingManifest.id);
	}

	const handleCancelManifestEdits = () => {
		cancelRequested(workingManifest.id);
		setWorkingManifest(JSON.parse(JSON.stringify(manifest ?? blankManifest)));
		setWorkingMaterials(buildWorkingMaterials(materials));
	}

	const handleSaveManifestEdits = async () => {
		manifestUpdated(workingManifest, getAssignmentChanges());
	}

	const handleManifestCheckBoxClick = (material: IWorkingMaterial) => {
		material.manifestId = material.manifestId ? null : workingManifest.id;
		setWorkingMaterials([...workingMaterials]);
	}
	//#endregion Event handlers

	//#region Action methods
	const getAssignmentChanges = (): IManifestMaterialAssignment[] => {
		let assignments: IManifestMaterialAssignment[] = [];
		for (const workingMaterial of workingMaterials) {
			if (workingMaterial.material.manifestId !== workingMaterial.manifestId) {
				assignments.push({ materialId: workingMaterial.material.id, manifestId: workingMaterial.manifestId });
			}
		}

		return assignments;
	}

	const getAssignedCount = (): number => {
		return workingMaterials.filter(m => m.manifestId === workingManifest.id).length;
	}

	const getUnassignedCount = (): number => {
		return workingMaterials.filter(m => m.manifestId == null).length;
	}

	const isManifestModified = (): boolean => {
		return workingManifest.number !== manifest?.number ||
			workingManifest.manifestVendorId !== manifest?.manifestVendorId ||
			workingManifest.secondaryTransporterId !== manifest?.secondaryTransporterId ||
			getAssignmentChanges().length > 0;
	}
	//#endregion Action methods

	return (
		(isDataReady &&
			<div className="manifest-form">
				{workingManifest.id != -1 &&
					<div className="header">
						<div className="title">
							{isEditing ? "Editing " : ""}Manifest: {workingManifest.number ? workingManifest.number : "N/A"}
						</div>
						{isLadingInEditableStatus &&
							<div className="controls">
								{isEditing ? (
									<>
										<Button variant="contained" color="primary" disableRipple disabled={!isManifestModified()} sx={{ transition: 'none' }} onClick={() => handleSaveManifestEdits()}>Save</Button>
										<Button variant="outlined" color="primary" disableRipple onClick={() => handleCancelManifestEdits()}>Cancel</Button>
									</>
								) : (
									<>
										<Button variant="outlined" color="primary" disableRipple sx={{ transition: 'none' }} onClick={() => handleEditManifest()}>Edit</Button>
										<Button variant="outlined" color="error" disableRipple onClick={() => handleDeleteManifest()}>Delete</Button>
									</>
								)}

							</div>
						}
					</div>
				}
				<div className="fields">
					<FormControl size="small" className="type">
						<div className="field-label">Type{workingManifest.id == -1 ? " *" : ""}</div>
						<Select
							value={workingManifest.isHazardous ? 1 : 0}
							required
							disabled={workingManifest.id != -1}
							onChange={(event) => { setWorkingManifest({ ...workingManifest, isHazardous: event.target.value ? true : false }) }}
						>
							<MenuItem value={1} key={1}>Hazardous</MenuItem>
							<MenuItem value={0} key={0}>Non Hazardous</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" className="number">
						<div className="field-label">Number{isEditing ? " *" : ""}</div>
						<TextField
							value={workingManifest?.number ?? ""}
							disabled={!isEditing}
							required
							placeholder='Required'
							inputProps={{ maxLength: 50 }}
							onChange={(event) => { setWorkingManifest({ ...workingManifest, number: event.target.value }) }}
						>
						</TextField>
					</FormControl>
					<FormControl size="small" className="manifest-vendor">
						<div className="field-label">Manifest Vendor</div>
						<Select
							value={workingManifest?.manifestVendorId ?? -1}
							disabled={!isEditing}
							onChange={(event) => { setWorkingManifest({ ...workingManifest, manifestVendorId: +event.target.value }) }}
							className={workingManifest?.manifestVendorId === -1 ? "subdued" : ""}
						>
							<MenuItem value={-1} key={-1} className="subdued">None</MenuItem>
							{manifestVendors.map((item, index) => (
								<MenuItem value={+item.value} key={index}>{item?.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" className="secondary-transporter">
						<div className="field-label">Secondary Transporter</div>
						<Select
							value={workingManifest.secondaryTransporterId ?? -1}
							disabled={!isEditing}
							onChange={(event) => { setWorkingManifest({ ...workingManifest, secondaryTransporterId: +event.target.value }) }}
							className={workingManifest?.secondaryTransporterId === -1 ? "subdued" : ""}
						>
							<MenuItem value={-1} key={-1} className="subdued">None</MenuItem>
							{secondaryTransporters.map((item, index) => (
								<MenuItem value={item.id} key={index}>{item?.name}</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>
				<div className="table">
					<div className="grid">
						<div className="headers">
							<div className="col-material">
								<p onClick={() => handleHeaderClick("material")}>
									Material
									{" "}
									{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-material-weight">
								<p onClick={() => handleHeaderClick("material-weight")}>
									Material WT
									{" "}
									{(sortField == "material-weight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "material-weight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-container">
								<p onClick={() => handleHeaderClick("container")}>
									Container
									{" "}
									{(sortField == "container" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "container" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-row">
								<p onClick={() => handleHeaderClick("row")}>
									Position
									{" "}
									{(sortField == "row" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
									{(sortField == "row" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
								</p>
							</div>
							<div className="col-controls"></div>
						</div>
						<div className="body">
							{workingMaterials && workingMaterials.filter(m => m.material.manifestId === workingManifest.id).length > 0 ? (
								<>
									{workingMaterials.filter(m => m.material.manifestId === workingManifest.id).map((item, index) => (
										<div className="row" key={index}>
											<div className="col-material">{item.material[materialNameProp]}</div>
											<div className="col-material-weight">{item.material[materialWeightProp]?.toLocaleString() ?? 0} {item.material[materialUOMAbbreviationProp] ?? ""}</div>
											<div className={`col-container ${item.material.containerName == "None" ? "inactive" : ""}`}>{item.material.containerName ? item.material.containerName.toLocaleString() : ""}{item.material.quantity > 1 ? ` x ${item.material.quantity}` : ""}</div>
											<div className="col-row">{item.material[positionProp]}</div>
											<div className="col-controls">
												{isEditing &&
													<Checkbox
														checked={item.manifestId ? true : false}
														disableRipple
														onChange={() => { handleManifestCheckBoxClick(item); }}
													></Checkbox>
												}
											</div>
										</div>
									))}
								</>
							) : (
								<>
									{!isEditing &&
										<div className="no-results">There are no materials on this manifest.</div>
									}
								</>
							)}
							{isEditing &&
								<>
									{isEditing && workingMaterials && workingMaterials.filter(m => !m.material.manifestId && (m.material[materialIsHazardousProp] == workingManifest?.isHazardous)).length > 0 ? (
										<>
											{workingMaterials.filter(m => !m.material.manifestId && (workingManifest?.isHazardous || m.material[materialIsHazardousProp] == workingManifest?.isHazardous)).map((item, index) => (
												<div className="row" key={index}>
													<div className="col-material">{item.material[materialNameProp]}</div>
													<div className="col-material-weight">{item.material[materialWeightProp]?.toLocaleString() ?? 0} {item.material[materialUOMAbbreviationProp] ?? ""}</div>
													<div className={`col-container ${item.material.containerName == "None" ? "inactive" : ""}`}>{item.material.containerName ? item.material.containerName.toLocaleString() : ""}{item.material.quantity > 1 ? ` x ${item.material.quantity}` : ""}</div>
													<div className="col-row">{item.material[positionProp]}</div>
													<div className="col-controls">
														{isEditing &&
															<Checkbox
																checked={item.manifestId ? true : false}
																disableRipple
																onChange={() => handleManifestCheckBoxClick(item)}
															></Checkbox>
														}
													</div>
												</div>
											))}
										</>
									) : (
										/* Only show if there are no material assigned to this manifest or any unassigned materials so there's literally nothing to work with */
										((workingMaterials.filter(m => m.material.manifestId === workingManifest.id).length == 0) &&
											<div className="no-results">There are no unassigned {!workingManifest?.isHazardous && <strong>non-hazardous </strong>}materials to add to this manifest.</div>
										)
									)}
								</>
							}
						</div>
					</div>
				</div>
				{workingManifest.id == -1 &&
					<div className="footer">
						<Button variant="contained" color="primary" disabled={!workingManifest.number.trim()} onClick={() => handleAddManifest()}>Add Manifest</Button>
					</div>
				}
				{/* <Json data={manifest}></Json>
				<Json data={workingManifest}></Json> */}
			</div>
		)
	);
}

export default MaterialManifestManager;